<script lang="ts">
	import type { PageData } from './$types'

	interface Props {
		data: PageData
	}

	let { data }: Props = $props()

	let title = $derived(data?.title)
	let description = $derived(data?.description)
</script>

<svelte:head>
	<title>{title}</title>
	<meta name="description" content={description} />
</svelte:head>

<div
	class="flex flex-col justify-center col-span-12 max-w-4xl prose px-4 lg:px-0 py-8 m-auto mx-auto my-10 lg:mb-32"
>
	<h2 class="text-4xl font-semibold">Terms of Service</h2>
	<p class="font-medium">Last updated: Feb 08, 2024</p>

	<h3 class="text-2xl font-medium">1. ACCEPTANCE OF TERMS</h3>

	<p>
		Airheart, Inc. (“<span class="underline">Airheart</span>”) provides technology-enabled services,
		including the website at{' '}
		<a href="https://www.airheart.com">https://www.airheart.com</a>, any Mobile Apps (as defined
		below), and other related software, content, and services, including all versions and upgrades
		thereto (collectively, the “<span class="underline">Services</span>"). Your use of the Services
		is subject to and governed by the terms and conditions in this Terms of Service (“<span
			class="underline">TOS</span
		>”). Airheart may, at its discretion, update this TOS at any time. You can access and review the
		most current version of this TOS at the URL for this page or by clicking on the “Terms of
		Service” link within the Services, or as otherwise made available by Airheart.
	</p>
	<p>
		PLEASE REVIEW THIS TOS CAREFULLY. BY REGISTERING FOR AN ACCOUNT OR OTHERWISE ACCESSING OR USING
		THE SERVICES, YOU AGREE TO BE BOUND BY THIS TOS, INCLUDING ANY UPDATES OR REVISIONS POSTED HERE
		OR OTHERWISE COMMUNICATED TO YOU. IF YOU DO NOT AGREE WITH THE TERMS AND CONDITIONS OF THIS TOS,
		YOU MAY NOT ACCESS OR USE THE SERVICES.
	</p>
	<p>
		<span class="font-semibold">
			THIS TOS REQUIRES FINAL AND BINDING ARBITRATION TO RESOLVE ANY DISPUTE OR CLAIM ARISING OUT OF
			OR RELATING IN ANY WAY TO THIS TOS, OR YOUR ACCESS TO OR USE OF THE SERVICES,
		</span>{' '}
		INCLUDING THE VALIDITY, APPLICABILITY OR INTERPRETATION OF THIS TOS, AND YOU AGREE THAT ANY SUCH
		CLAIM WILL BE RESOLVED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A CLASS, CONSOLIDATED OR REPRESENTATIVE
		ACTION, ARBITRATION OR OTHER SIMILAR PROCESS. PLEASE REVIEW SECTION 13 CAREFULLY TO UNDERSTAND YOUR
		RIGHTS AND OBLIGATIONS WITH RESPECT TO THE RESOLUTION OF ANY CLAIM.
	</p>
	<p>
		You represent and warrant that you: (a) are of legal age to form a binding contract; (b) have
		the right, authority, and capacity to agree to and abide by this TOS; and (c) are not a person
		barred from using the Services under the laws of any applicable jurisdiction. THE SERVICES ARE
		NOT INTENDED FOR USERS UNDER THE AGE OF 18, AND SUCH USERS ARE EXPRESSLY PROHIBITED FROM
		SUBMITTING ANY PERSONAL DATA OR USING ANY ASPECT OF THE SERVICES, AND BY TAKING SUCH ACTIONS YOU
		AGREE, REPRESENT, AND WARRANT THAT YOU ARE 18 YEARS OF AGE OR OLDER.{' '}
	</p>
	<h3 class="text-2xl font-medium">2. RIGHTS</h3>
	<p>
		(a) <span class="font-semibold">Grant.</span> Subject to and conditioned on your compliance with
		this TOS, Airheart hereby grants you a personal, non-exclusive, non-transferable, non-sublicensable,
		revocable license solely to use the Services only for your personal, non-commercial use and, solely
		with respect to Trips (as defined herein) for the explicit purposes stated herein. Your access to
		and use of the Services must further comply in all material respects with all usage guidelines posted
		by Airheart
	</p>
	<p>
		(b) <span class="font-semibold">Mobile Apps.</span> Airheart may make available mobile software
		applications for access to and use of certain components of the Services (collectively, “<span
			class="underline">Mobile Apps</span
		>”). Your access to and use of Mobile Apps is subject to and governed by this TOS. If any Mobile
		App is downloaded by you from the iTunes App Store (each, an “<span class="underline"
			>iOS Mobile App</span
		>”), your use of such iOS Mobile App is further subject to your compliance in all material
		respects with the terms and conditions of the Usage Rules set forth in the iTunes App Store
		Terms of Service. This TOS is between you and Airheart only, and not with Apple Inc. (“
		<span class="underline">Apple</span>”) or any other app store provider, and Apple is not
		responsible for iOS Mobile Apps and the contents thereof; however, Apple and Apple’s
		subsidiaries are third-party beneficiaries of this TOS with respect to iOS Mobile Apps.
	</p>
	<p>
		(c) <span class="font-semibold">Trademarks.</span> You may not use the Airheart names, brands,
		trademarks, service marks and logos that Airheart makes available on the Services (“
		<span class="underline">Marks</span>”). Airheart claims trademark protection over all such Marks
		and you will not use the Marks except as expressly authorized herein. You will not remove or
		alter the Marks or any proprietary notices on the Services. The Marks may not be included in or
		as part of any registered corporate name, any other logo, or service or product name. You may
		not create any derivative works of the Marks or use the Marks in a manner that creates or
		reasonably implies an inaccurate sense of endorsement, sponsorship, or association with
		Airheart. You will not otherwise use business names or logos in a manner that can mislead,
		confuse, or deceive any third party. All use of the Marks and all goodwill arising out of such
		use, will inure to Airheart’s benefit.
	</p>
	<h3 class="text-2xl font-medium">3. CREATOR PROGRAM TERMS</h3>
	<p>
		(a) If you use the Services to create and share trips (“
		<span class="underline">Trips</span>”) and travel content, you are referred to herein as a “<span
			class="underline">Creator</span
		>.” Airheart reserves the right to approve, reject or remove Creator privileges at any time for
		any reason.
	</p>
	<p>
		(b) You agree to follow any and all usage guidelines posted by Airheart on the Services,
		contained herein or otherwise communicated to you by Airheart. You further acknowledge and agree
		that any content, schedules, information, descriptions or any other content contained in any
		Trips you provide, are your sole responsibility.{' '}
	</p>
	<p>
		(c) Airheart will pay you a commission rate of 80% with each sale of an Trip. Airheart shall
		make commercially reasonable efforts to pay Commissions to you within seven (7) days of the end
		of each month following the purchase of the trip, but assumes no liability for any delays in any
		processing or payments of any Commissions. You agree that you are solely responsible for paying
		any and all taxes due in connection with your receipt of any such Commission.
	</p>
	<p>
		(d) Commissions shall be paid to you in a commercially reasonable timeframe. By becoming a
		Creator, you expressly authorize Airheart and the Services to access your bank account and
		associated information (“<span class="underline">Creator Account</span>”), on your behalf and as
		your limited agent, in order to pay you Commissions. You are exclusively and solely responsible
		for all payments generated, authorized and electronically transferred in connection with the
		Commissions. Airheart shall not be liable or responsible in any way for the accuracy or validity
		of any Creator Account or banking information, including your account and routing numbers and/or
		who is authorized to access this information. Airheart is not liable to you or any third-party,
		banking institution, or vendor for any unauthorized or incorrect payments generated, authorized,
		printed, or mailed in connection with the Commissions, nor any fees assessed to you, any
		vendors, or any bank due to unauthorized, incorrect, or returned payments. Airheart is not
		liable or responsible for verifying addresses or Creator Account information inputted in
		connection with the Commissions. You shall verify all addresses and banking account information
		submitted to Airheart. You further authorize Airheart to share your identity and Creator Account
		data with Dwolla, Inc. (“<span class="font-semibold">Dwolla</span>”) for purposes of providing
		the Commissions and you are responsible for the accuracy and completeness of such data.
		Furthermore, you authorize Dwolla to share the Creator Account number and routing number with
		Airheart for the purpose of Airheart’s provision of Services to you, subject to Airheart’s
		Privacy Policy. You are solely responsible for complying with any terms set by your bank or
		credit union with respect to the Creator Account, including any fee terms.
	</p>
	<p>
		(e) You may de-list such Trips, provided however, that Airheart shall keep any such Trips up on
		the Services and remove the option to purchase such Trip, until the last customer who has
		purchased such Trip has completed the items contained within such Trip.
	</p>
	<p>
		(f) You hereby grant to Airheart and its affiliates, successors, licensees and assigns, the
		right to use your name, image and likeness (including information you provide to Airheart and
		any other information about you that is publicly available) in connection with any such Trip and
		Airheart’s promotion of any such Trip, and any derivative works Airheart makes from any such
		Trip, including to advertise and promote the same or any product or service that features or
		includes any portion of a Trip. You waive the right to inspect or approve any use of your name,
		image or likeness as contemplated by this TOS.
	</p>
	<p>
		(g) You hereby acknowledge and agree that as a Creator, your relationship to Airheart is one of
		an independent contractor and not as a subcontractor, agent of employee of Airheart. This TOS
		does not establish a joint venture, partnership or other business association or fiduciary
		relationship between you and Airheart. You do not have the authority to make any representations
		for Airheart or to bind Airheart by virtue of this TOS. Airheart is not responsible for
		providing you with any insurance or benefits, including unemployment or worker’s compensation
		insurance.{' '}
	</p>
	<h3 class="text-2xl font-medium">4. PRIVACY POLICY</h3>
	<p>
		In addition to this TOS, the Airheart{' '}
		<a href="https://airheart.com/legal/privacy" data-testid="PrivacyPolicyLink1">Privacy Policy</a>
		at{' '}
		<a href="https://airheart.com/legal/privacy" data-testid="PrivacyPolicyLink2"
			>https://airheart.com/legal/privacy</a
		>{' '}
		(“Privacy Policy”) applies to how Airheart may process information provided as part of the Services.
		You acknowledge and agree that by accessing or using the Services, Airheart may receive certain information
		about you, including personal data, as set forth in the Privacy Policy, and Airheart may collect,
		use, disclose, store, share, and process such personal data in accordance with such Privacy Policy.
	</p>
	<h3 class="text-2xl font-medium">5. PROPRIETARY RIGHTS</h3>
	<p>
		(a) You grant Airheart and its service providers a perpetual, irrevocable, worldwide,
		royalty-free, fully-paid-up, non-exclusive, sublicensable, transferable license to use,
		reproduce, modify, adapt, create derivative works from, publicly perform, publicly display,
		distribute, make and have made all content (in any form and any medium, whether now known or
		later developed) that you provide in connection with the Services, including any Trips you
		provide and any photos you provide in connection therewith. You acknowledge and agree that the
		technical processing and transmission of data associated with the Services, may require: (i)
		transmissions over various networks and across borders; and (ii) modifications to conform,
		connect, and adapt to technical requirements of networks or devices.
	</p>
	<p>
		(b) The Services provided to you hereunder or available to you through the Services are
		licensed, not sold, and Airheart retains and reserves all rights not expressly granted in this
		TOS. You acknowledge and agree that, as between you and Airheart, Airheart and its licensors
		owns all rights, title and interest (including all intellectual property rights) in the Services
		and all data, content, and other materials within the Services. The Services are protected by
		U.S. and international copyright and other intellectual property laws and treaties. Airheart
		reserves all rights not expressly granted to you in this TOS.
	</p>
	<p>
		(c) You may not share your account or password with anyone. You are fully responsible for all
		activities that occur under your account. You agree to notify Airheart immediately of any
		unauthorized use of your account or password or any other similar breach of security. If your
		account remains inactive for three months or longer, Airheart reserves the right to suspend or
		terminate your account, with or without notice to you, and delete your content all without
		liability.
	</p>
	<h3 class="text-2xl font-medium">6. USER CONDUCT AND RESTRICTIONS</h3>
	<p>(a) In your use of the Services, you will not:</p>
	<ol>
		<li>
			use, reproduce, modify, adapt, create derivative works from, sublicense, publicly perform,
			publicly display, distribute, sell, lease, rent, make, have made, assign, pledge, transfer or
			otherwise grant rights to the Services, except as expressly permitted under this TOS;{' '}
		</li>
		<li>
			reverse engineer, disassemble, decompile, translate, or otherwise attempt to derive trade
			secrets, algorithms, or the source code, architectural framework, or data records, within or
			associated with the Services;{' '}
		</li>
		<li>
			interfere with or disrupt the integrity or performance of the Services, including by
			disrupting the ability of any other person to use or enjoy the Services;{' '}
		</li>
		<li>
			provide use of the Services on a service bureau, rental or managed services basis, provide or
			permit other individuals or entities to create Internet "links" to the Services or "frame" or
			"mirror" the Services on any other server, or wireless or Internet-based device;{' '}
		</li>
		<li>
			access the Services for the purpose of developing, marketing, selling or distributing any
			product or service that competes with or includes features substantially similar to the
			Services;{' '}
		</li>
		<li>
			violate any applicable local, state, provincial, federal or international law or regulation,
			or use the Services for any illegal, unauthorized or otherwise improper purposes, including to
			store or transmit malicious code, or to store or transmit material in violation of third-party
			privacy rights;{' '}
		</li>
		<li>remove or obscure any proprietary notice that appears within the Services;</li>
		<li>
			impersonate any person or entity, including Airheart personnel, or falsely state or otherwise
			misrepresent your affiliation with Airheart, or any other entity or person;
		</li>
		<li>
			forge headers or otherwise manipulate identifiers in order to disguise the origin of any
			content transmitted through the Services;
		</li>
		<li>
			take any action that imposes an unreasonable or disproportionately heavy load on the Services
			or its infrastructure; or
		</li>
		<li>
			use spiders, crawlers, robots, scrapers, automated tools or any other similar means to access
			the Services; or download, reproduce, or archive any substantial portion of the Services.
		</li>
	</ol>
	<p>
		(b) You will not: upload, post, email, store, transmit, or otherwise make available any content
		that:{' '}
	</p>
	<ol>
		<li>
			is illegal, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene,
			libelous, invasive of another's privacy, hateful, or otherwise objectionable;{' '}
		</li>
		<li>
			may not be made available under any law or under contractual or fiduciary relationships (such
			as confidential or proprietary information learned as part of an employment relationship or
			under a non-disclosure agreement);{' '}
		</li>
		<li>
			infringes any patent, trademark, trade secret, copyright, or other proprietary right of any
			party;{' '}
		</li>
		<li>
			consists of unsolicited or unauthorized advertising, promotional materials, junk mail, spam,
			chain letters, pyramid schemes, commercial electronic messages, or any other form of
			solicitation;{' '}
		</li>
		<li>
			contains software viruses or any other code, files or programs designed to interrupt, destroy
			or limit the functionality of any software or hardware;{' '}
		</li>
		<li>contains infringing, libelous, or otherwise unlawful or tortious material; or</li>
		<li>consists of information that you know or have reason to know is false or inaccurate.</li>
	</ol>
	<p>
		(c) Airheart’s failure to enforce any of these restrictions or guidelines shall not act as a
		waiver for any future enforcement, will not be considered a breach of this TOS by Airheart, and
		does not create a private right of action for any other party.
	</p>
	<h3 class="text-2xl font-medium">7. SALES TERMS</h3>
	<p>
		(a) <span class="font-semibold">Supported Locations.</span> Airheart is currently only selling
		Trips to users within the United States.{' '}
	</p>
	<p>
		(b) <span class="font-semibold">Payment.</span> You shall pay all agreed upon fees for all
		products ordered through the Services, including Trips, as set forth in the order confirmation
		page (“<span class="underline">Fees</span>”) in accordance with terms set forth herein and any
		additional terms set forth on the Services. You hereby authorize Airheart to charge your credit
		card on file for all Fees that have become due and payable.{' '}
	</p>
	<p>
		(c) <span class="font-semibold">Third Party Providers.</span> The Services may include access to
		third party services and offerings connected with Trips and other content provided on the
		Services. Any such terms connected with any bookings will be provided at the time of booking and
		shall be set out in your booking confirmation email, including the Supplier Terms &amp;
		Conditions located at:{' '}
		<a
			rel="nofollow noopener noreferrer external"
			target="_blank"
			href="https://developer.expediapartnersolutions.com/terms/en"
		>
			https://developer.expediapartnersolutions.com/terms/en
		</a>
		.
	</p>
	<p>
		(d) <span class="font-semibold">Refunds.</span> No refunds for Trips shall be provided. Refunds for
		third party services and content provided through the Services shall be provided in accordance with
		the refund policies of any such third party provider.
	</p>
	<p>
		(e) <span class="font-semibold">Taxes.</span> Unless otherwise noted, items sold by Airheart are
		subject to state and local sales tax in accordance with applicable laws. Airheart may not collect
		sales or use taxes in all states. For states imposing sales or use taxes, your purchase is subject
		to use tax unless it is specifically exempt from taxation. Your purchase is not exempt merely because
		it is made over the Internet or by other remote means. Many states require purchasers to file a sales/use
		tax return at the end of the year reporting all of the taxable purchases that were not taxed and
		to pay tax on those purchases. Details of how to file these returns may be found at the websites
		of your respective taxing authorities. The sales tax indicated on the checkout page is an estimate.
		The sales tax ultimately charged to your credit card will be calculated when your credit card charge
		is authorized and will reflect applicable state and local taxes.
	</p>
	<p>
		(f) <span class="font-semibold">Merchant of Record.</span> Airheart, and not the applicable Creator,
		shall be the merchant of record for any and all sales of Itineraries through the Services. Where
		applicable, Airheart’s travel agent licenses and/or bonds shall be applicable with respect to Itinerary
		sales through the Services.
	</p>

	<h3 class="text-2xl font-medium">8. FEEDBACK</h3>

	<p>
		If you elect to provide or make available to Airheart any suggestions, comments, ideas,
		improvements or other feedback relating to the Services (“
		<span class="underline">Feedback</span>”), Airheart shall own and be free to use, reproduce,
		modify, adapt, create derivative works from, publicly perform, publicly display, distribute,
		make, have made, assign, pledge, transfer or otherwise grant rights in your Feedback in any form
		and any medium (whether now known or later developed), without credit or compensation to you.{' '}
	</p>

	<h3 class="text-2xl font-medium">9. DEALINGS WITH ADVERTISERS AND OTHER THIRD PARTIES</h3>

	<p>
		The Services may include or provide access to third party products, services, content, or
		offerings, including advertising for such (“
		<span class="underline">Third Party Services</span>”). You acknowledge that different terms of
		use and privacy policies may apply to your use of such Third Party Services and that terms and
		policies are solely between you and the advertiser or other third party. You agree that does not
		endorse and is not responsible or liable for any issues related to Third Party Services.{' '}
	</p>

	<h3 class="text-2xl font-medium">10. INDEMNIFICATION</h3>

	<p>
		You shall indemnify and hold Airheart and its affiliates, and each of their officers, directors,
		employees, agents, partners and licensors (collectively, “
		<span class="underline">Airheart Parties</span>”) harmless from and against all losses, damages,
		costs, liabilities, and expenses, including reasonable attorneys’ fees, to extent resulting from
		or arising out of any third party claim, demand, or action due to (a) content you provide to
		Airheart, including Trips; (b) your violation of this TOS, any law or regulation, or any rights
		(including intellectual property rights) of another party; or (c) your use of the Services,
		except as expressly permitted in this TOS.
	</p>

	<h3 class="text-2xl font-medium">11. DISCLAIMER OF WARRANTIES</h3>

	<p>
		(a) YOUR USE OF THE SERVICES IS AT YOUR SOLE RISK. THE SERVICES ARE PROVIDED ON AN “AS IS” AND
		“AS AVAILABLE” BASIS, WITH ALL FAULTS. TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW,
		Airheart PARTIES EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED OR
		ARISING FROM STATUTE, COURSE OF DEALING, USAGE OF TRADE OR OTHERWISE, INCLUDING THE IMPLIED
		WARRANTIES OF MERCHANTABILITY, QUALITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND
		NON-INFRINGEMENT.
	</p>
	<p>
		(b) Airheart PARTIES MAKE NO WARRANTY OR REPRESENTATION THAT: (i) THE SERVICES WILL MEET YOUR
		REQUIREMENTS; (ii) ACCESS TO THE SERVICES WILL BE UNINTERRUPTED, TIMELY, SECURE, VIRUS-FREE, OR
		ERROR-FREE; OR (iii) THE INFORMATION AND ANY RESULTS THAT MAY BE OBTAINED FROM ACCESS TO OR USE
		OF THE SERVICES WILL BE ACCURATE, RELIABLE, CURRENT, OR COMPLETE.
	</p>
	<p>
		(c) ALL CONTENT MADE AVAILABLE THROUGH THE SERVICES IS MADE AVAILABLE FOR INFORMATIONAL PURPOSES
		ONLY. YOU ARE SOLELY RESPONSIBLE FOR CONFIRMING THE ACCURACY OF ALL CONTENT BEFORE TAKING OR
		OMITTING ANY ACTION.
	</p>
	<p>
		(d) THIS DISCLAIMER OF WARRANTY MAY NOT BE VALID IN SOME JURISDICTIONS AND YOU MAY HAVE WARRANTY
		RIGHTS UNDER LAW WHICH MAY NOT BE WAIVED OR DISCLAIMED. ANY SUCH WARRANTY EXTENDS ONLY FOR
		THIRTY (30) DAYS FROM THE EFFECTIVE DATE OF THIS AGREEMENT (UNLESS SUCH LAW PROVIDES OTHERWISE).
	</p>

	<h3 class="text-2xl font-medium">12. LIMITATION OF LIABILITY</h3>
	<p>
		(a) Airheart PARTIES SHALL NOT BE LIABLE FOR ANY LOST PROFITS, LOSS OF DATA OR GOODWILL, OR COST
		OF COVER, OR FOR ANY INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, PUNITIVE, OR CONSEQUENTIAL
		DAMAGES, INCLUDING DAMAGES ARISING FROM ANY TYPE OR MANNER OF COMMERCIAL, BUSINESS, OR FINANCIAL
		LOSS, EVEN IF Airheart PARTIES HAD ACTUAL OR CONSTRUCTIVE KNOWLEDGE OF THE POSSIBILITY OF SUCH
		DAMAGES AND REGARDLESS OF WHETHER SUCH DAMAGES WERE FORESEEABLE. IN NO EVENT SHALL Airheart
		PARTIES’ TOTAL LIABILITY TO YOU FOR ANY AND ALL CLAIMS ARISING FROM OR RELATING TO THIS TOS OR
		YOUR ACCESS TO OR USE OF (OR INABILITY TO ACCESS OR USE) THE SERVICES OR CONTENT EXCEED THE
		AMOUNT PAID BY YOU TO Airheart FOR ACCESS TO THE SERVICES WITHIN THE TWELVE (12) MONTHS
		IMMEDIATELY PRECEDING THE DATE ON WHICH THE APPLICABLE CLAIM(S) AROSE.
	</p>
	<p>
		(b) THE FOREGOING LIMITATIONS, EXCLUSIONS AND DISCLAIMERS SHALL APPLY REGARDLESS OF WHETHER SUCH
		LIABILITY ARISES FROM ANY CLAIM BASED UPON CONTRACT, WARRANTY, TORT (INCLUDING NEGLIGENCE),
		STRICT LIABILITY OR OTHERWISE, AND WHETHER OR NOT THE PARTY HAS BEEN ADVISED OF THE POSSIBILITY
		OF SUCH LOSS OR DAMAGE. INSOFAR AS APPLICABLE LAW PROHIBITS ANY LIMITATION ON LIABILITY HEREIN,
		THE PARTIES AGREE THAT SUCH LIMITATION WILL BE AUTOMATICALLY MODIFIED, BUT ONLY TO THE EXTENT SO
		AS TO MAKE THE LIMITATION COMPLIANT WITH APPLICABLE LAW. THE PARTIES AGREE THAT THE LIMITATIONS
		ON LIABILITIES SET FORTH HEREIN ARE AGREED ALLOCATIONS OF RISK AND SUCH LIMITATIONS WILL APPLY
		NOTWITHSTANDING THE FAILURE OF ESSENTIAL PURPOSE OF ANY LIMITED REMEDY.
	</p>

	<h3 class="text-2xl font-medium">13. SUSPENSION AND TERMINATION</h3>

	<p>(a) Either party may terminate this TOS at any time.</p>
	<p>
		(b) If you violate this TOS, Airheart may, with or without notice to you, immediately suspend or
		terminate your access and use of the Services.
	</p>
	<p>
		(c) Airheart reserves the right at any time to modify, suspend, or discontinue the Services (or
		any portion thereof) with or without notice, and Airheart shall not be liable to you or any
		third party for any such modification or discontinuance;
	</p>
	<p>
		(d) Upon termination of this TOS for any reason: (i) Airheart, in its sole discretion, may
		remove and discard your content and information; (ii) you will immediately cease your use of the
		Services; and (iii) any provision that, by its terms, is intended to survive the expiration or
		termination of this TOS shall survive such expiration or termination. Further, you agree that
		that Airheart shall not be liable to you or any third party for any termination of your account
		or access to the Services.{' '}
	</p>
	<h3 class="text-2xl font-medium">14. GOVERNING LAW</h3>
	<p>
		This TOS shall be governed by and construed and enforced in accordance with the United States
		Federal Arbitration Act, other applicable federal laws and the laws of the State of Texas,
		without regard to conflict of laws principles. The parties agree that neither the United Nations
		Convention on Contracts for the International Sale of Goods, nor the Uniform Computer
		Information Transaction Act (UCITA) shall apply to this TOS, regardless of the states in which
		the parties do business or are incorporated.
	</p>
	<h3 class="text-2xl font-medium">15. BINDING ARBITRATION AND CLASS ACTION WAIVER</h3>
	<p>
		(a) ALL CLAIMS ARISING IN CONNECTION WITH THIS AGREEMENT SHALL BE RESOLVED BY FINAL AND BINDING
		ARBITRATION RATHER THAN IN COURT, EXCEPT THAT YOU MAY ASSERT CLAIMS IN SMALL CLAIMS COURT
		(DEFINED FOR THE PURPOSES OF THIS TOS AS A COURT OF LIMITED JURISDICTION THAT MAY ONLY HEAR
		CLAIMS NOT EXCEEDING $5,000) IF YOUR CLAIMS ARE WITHIN THE COURT’S JURISDICTION. THERE IS NO
		JUDGE OR JURY IN ARBITRATION, AND COURT REVIEW OF AN ARBITRATION AWARD IS LIMITED.
	</p>
	<p>
		(b) The arbitration shall be conducted by the American Arbitration Association (AAA) under its
		then-applicable Commercial Arbitration Rules or, as appropriate, its Consumer Arbitration Rules.
		The AAA’s rules are available at{' '}
		<a rel="nofollow noopener noreferrer external" target="_blank" href="https://www.adr.org/">
			https://www.adr.org/
		</a>
		. Payment of all filing, administration and arbitrator fees shall be governed by the AAA’s rules.
		The arbitration shall be conducted in the English language by a single independent and neutral arbitrator.
		For any hearing conducted in person as part of the arbitration, you agree that such hearing shall
		be conducted in Austin, Texas or, if the Consumer Arbitration Rules apply, another location reasonably
		convenient to both parties with due consideration of their ability to travel and other pertinent
		circumstances, as determined by the arbitrator. The decision of the arbitrator shall be final and
		binding. Judgment on the arbitral award may be entered in any court of competent jurisdiction.
	</p>
	<p>
		(c) WE EACH AGREE THAT ALL CLAIMS SHALL BE RESOLVED ONLY ON AN INDIVIDUAL BASIS AND NOT IN A
		CLASS, CONSOLIDATED OR REPRESENTATIVE ACTION OR OTHER SIMILAR PROCESS (INCLUDING ARBITRATION).
		IF FOR ANY REASON A CLAIM PROCEEDS IN COURT RATHER THAN IN ARBITRATION, WE EACH WAIVE ANY RIGHT
		TO A JURY TRIAL AND AGREE THAT SUCH CLAIM SHALL BE BROUGHT ONLY IN A COURT OF COMPETENT
		JURISDICTION IN AUSTIN, TEXAS. YOU HEREBY SUBMIT TO THE PERSONAL JURISDICTION AND VENUE OF SUCH
		COURTS AND WAIVE ANY OBJECTION ON THE GROUNDS OF VENUE, FORUM NON-CONVENIENS OR ANY SIMILAR
		GROUNDS WITH RESPECT TO ANY SUCH CLAIM.
	</p>
	<p>
		(d) Notwithstanding anything to the contrary, you and Airheart may seek injunctive relief and
		any other equitable remedies from any court of competent jurisdiction to protect its
		intellectual property rights, whether in aid of, pending, or independently of the resolution of
		any dispute pursuant to the arbitration procedures set forth in this Section 13.
	</p>
	<p>
		(e) If Airheart implements any material change to this Section 13, such change shall not apply
		to any Claim for which you provided written notice to Airheart before the implementation of the
		change.
	</p>

	<h3 class="text-2xl font-medium">16. LEGAL COMPLIANCE</h3>
	<p>
		You represent and warrant that you will comply with all applicable foreign, federal, state, and
		local laws, rules and regulations, including without limitation, U.S. export laws and import and
		use laws of the country where Licensed Material is delivered or used and you are not: (a)
		located in a country that is subject to a U.S. Government embargo, or designated by the U.S.
		Government as a “terrorist supporting” country; and (b) listed on any U.S. Government list of
		prohibited or restricted parties, including the Specially Designated Nationals List.
	</p>
	<h3 class="text-2xl font-medium">17. U.S. GOVERNMENT ENTITIES</h3>
	<p>
		This section applies to access to or use of the Services by a branch or agency of the United
		States Government. The Services includes “commercial computer software” and “commercial computer
		software documentation” as such terms are used in 48 C.F.R. 12.212 and qualifies as “commercial
		items” as defined in 48 C.F.R. 2.101. Such items are provided to the United States Government:
		(a) for acquisition by or on behalf of civilian agencies, consistent with the policy set forth
		in 48 C.F.R. 12.212; or (b) for acquisition by or on behalf of units of the Department of
		Defense, consistent with the policies set forth in 48 C.F.R. 227.7202-1 and 227.7202-3. The
		United States Government shall acquire only those rights set forth in this TOS with respect to
		the such items, and any access to or use of the Services by the United States Government
		constitutes: (i) agreement by the United States Government that that such items are “commercial
		computer software” and “commercial computer software documentation” as defined in this section;
		and (ii) acceptance of the rights and obligations herein.
	</p>
	<h3 class="text-2xl font-medium">18. PROCEDURE FOR MAKING CLAIMS OF COPYRIGHT INFRINGEMENT</h3>
	<p>
		If you believe that your work has been made available through the Services in a way that
		constitutes copyright infringement, please provide Airheart’s Agent for Notice of Copyright
		Claims the following information: (a) a physical or electronic signature of a person authorized
		to act on behalf of the owner of an exclusive right that is allegedly infringed; (b) a
		description of the copyrighted work claimed to have been infringed, or, if multiple copyrighted
		works are covered by a single notification, a representative list of such works; (c) a
		description of the material that you claim is infringing and where that material may be accessed
		within the Services; (d) your address, telephone number and email address; (e) a statement by
		you that you have a good-faith belief that use of the material in the manner complained of is
		not authorized by the copyright owner, its agent or the law; and (f) a statement from you that
		the information in the notification is accurate and, under penalty of perjury, that you are
		authorized to act on behalf of the owner of an exclusive right that is allegedly infringed. If
		you believe in good faith that a notice of copyright infringement has been wrongly filed against
		you, please contact Airheart’s Agent for Notice of Copyright Claims. Airheart’s Agent for Notice
		of Copyright Claims can be reached as follows:
	</p>
	<p>Agent for Notice of Copyright Claims</p>
	<p class="p-0 m-0">1401 Lavaca Street Unit #284 Austin, TX 78701</p>
	<p class="p-0 m-0">+1 877-595-3537</p>
	<p class="p-0 m-0">support@airheart.com</p>

	<h3 class="text-2xl font-medium">19. CALIFORNIA USERS & RESIDENTS</h3>
	<p>
		In accordance with California Civil Code §1789.3, you may report complaints to the Complaint
		Assistance Unit of the Division of Consumer Services of the California Department of Consumer
		Affairs by contacting such unit in writing at 1625 North Market Blvd., Suite N 112, Sacramento,
		CA 95834, or by telephone at +1 877-595-3537.
	</p>
	<h3 class="text-2xl font-medium">20. GENERAL PROVISIONS</h3>
	<p>
		This TOS constitutes the entire agreement between you and Airheart concerning your access to and
		use of the Services. It supersedes all prior and contemporaneous oral or written negotiations
		and agreements between you and Airheart with respect to such subject matter. In the event of any
		conflict between or among this TOS and any end user license agreement, privacy policy or usage
		guidelines to which this TOS refers, the terms and conditions of this TOS shall take precedence
		and govern. This TOS may not be amended by you except in a writing executed by you and an
		authorized representative of Airheart. Except as otherwise expressly provided in this TOS, there
		shall be no third-party beneficiaries to this TOS. For the purposes of this TOS, the words “such
		as,” “include,” “includes” and “including” shall be deemed to be followed by the words “without
		limitation.” You may not assign or delegate any right or obligation under this TOS without the
		prior written consent of Airheart. The failure of Airheart to exercise or enforce any right or
		provision of this TOS shall not constitute a waiver of such right or provision. If any provision
		of this TOS is held to be invalid or unenforceable under applicable law, then such provision
		shall be construed, limited, modified or, if necessary, severed to the extent necessary to
		eliminate its invalidity or unenforceability, without in any way affecting the remaining parts
		of this TOS. Any prevention of or delay in performance by Airheart hereunder due to labor
		disputes, acts of god, failure of the Internet, governmental restrictions, enemy or hostile
		governmental action, fire or other casualty or other causes beyond its reasonable control shall
		excuse the performance of its obligations for a period equal to the duration of any such
		prevention or delay.
	</p>
</div>
